import { useSetAtom } from "jotai";
import { type FunctionComponent, startTransition, useEffect, useState } from "react";

import { useFallbackStorageValue, useResponsive } from "shared/hooks";
import { theme } from "shared/lib/styles";
import { yieldToMain } from "shared/lib/utils";

import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";

import { gdprAtom } from "./atoms";
import { useGeo } from "./hooks";
import { Button, Container, Text } from "./styles";

const GDPR_KEY = "gdpr_202006";

export const GDPRBanner: FunctionComponent = () => {
  const { set: setIsAcceptedGDPR, value: isAcceptedGDPR } = useFallbackStorageValue(GDPR_KEY, {
    defaultValue: false,
  });
  const setGDPR = useSetAtom(gdprAtom);

  const [shouldShow, setShouldShow] = useState(false);
  const { isGeoLoaded, shouldShowGDPRByGeo } = useGeo();

  const isMobile = useResponsive(theme.breakpoints.only("mobile"), true);

  const handleGDPRBannerClose = () => {
    void (async () => {
      await yieldToMain({ priority: "user-blocking" });
      startTransition(() => {
        setShouldShow(false);
        setIsAcceptedGDPR(true);
      });
    })();

    void (async () => {
      await yieldToMain({ priority: "background" });
      sendGA4Tracking({
        action: "click",
        category: "gdpr",
        subcategory: "",
      });

      // In this case, the user accept ed the GDPR
      // isAccepted' will be 'true' but the 'isNextSession' will be 'false'
      setGDPR({
        isAccepted: true,
      });

      window.postMessage("CLOSE_GDPR", window.location.origin);
    })();
  };

  const handleGDPRBannerOpen = () => {
    setShouldShow(true);
  };

  useEffect(() => {
    if (!isGeoLoaded) return;

    if (isAcceptedGDPR || (isMobile && !shouldShowGDPRByGeo)) {
      setGDPR({
        isAccepted: true,
      });
      return;
    }

    if ("requestIdleCallback" in window) {
      requestIdleCallback(handleGDPRBannerOpen, { timeout: 3000 });
    } else {
      setTimeout(handleGDPRBannerOpen, 3000);
    }
  }, [isAcceptedGDPR, setGDPR, isMobile, shouldShowGDPRByGeo, isGeoLoaded]);

  return (
    <Container $shouldShow={shouldShow}>
      <Text>
        We use cookies to tailor your experience and present relevant ads. By clicking “
        <b>Accept</b>
        ”, you agree that cookies can be placed per our{" "}
        <a href="https://www.scmp.com/privacy-policy" rel="noreferrer" target="_blank">
          Privacy Policy
        </a>
      </Text>
      {/** the class "gdpr-banner__accept" is used to trigger
       * "GDPR Banner Accept Click" in the Google Tag Manager,
       * which is used to trigger "Consent Update Command - Ad Storage Granted" */}
      <Button className="gdpr-banner__accept" onClick={handleGDPRBannerClose}>
        ACCEPT
      </Button>
    </Container>
  );
};
